/* eslint react-hooks/exhaustive-deps: "warn" */
import { type MouseEvent, useEffect, useRef } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';

import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './DataLicensingRedistributionRights.module.scss';

type Translate = (key: string) => string;

type Right = {
  label: string;
  ours: string;
  typical: string;
};

const getRights = (t: Translate): Right[] => [
  {
    label: t('Redistribution to your end customers'),
    ours: t('Granted in the base licence'),
    typical: t('Internal use only, or a per-seat surcharge'),
  },
  {
    label: t('Territory'),
    ours: t('Worldwide, every market you sell into'),
    typical: t('Scoped by region and repriced as you expand'),
  },
  {
    label: t('Sublicensing to your own resellers'),
    ours: t('Granted where the channel is named'),
    typical: t('Case by case, vendor approval per partner'),
  },
  {
    label: t('AI training and model development'),
    ours: t('Licensed in writing, retrieval and evaluation included'),
    typical: t('Prohibited by default, silent on retrieval and evaluation'),
  },
  {
    label: t('Records after termination'),
    ours: t('Perpetual for records delivered in-term'),
    typical: t('Deletion within a fixed window, certified in writing'),
  },
  {
    label: t('Source attribution'),
    ours: t('Registry named on every field'),
    typical: t('Normalised output, origin removed'),
  },
  {
    label: t('Field-level dating'),
    ours: t('Filing date and retrieval timestamp, per field'),
    typical: t('A single file-level refresh date, if any'),
  },
  {
    label: t('Identifiers'),
    ours: t('Open registry identifiers alongside ours'),
    typical: t('Proprietary keys, resolvable only by the vendor'),
  },
];

export const DataLicensingRedistributionRights = () => {
  const { t } = useInt();
  const rights = getRights(t);
  const headRef = useRef<HTMLDivElement>(null);
  const tableWrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const revealables = ([headRef.current, tableWrapRef.current] as (HTMLElement | null)[]).filter(
      (el): el is HTMLElement => Boolean(el),
    );

    if (!revealables.length) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add(styles.revealed);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 },
    );

    revealables.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const handleSpotlight = (e: MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty('--mx', `${e.clientX - rect.left}px`);
    e.currentTarget.style.setProperty('--my', `${e.clientY - rect.top}px`);
  };

  return (
    <Section background="#0E1526" className={styles.root}>
      <div className={styles.glow} aria-hidden="true" />

      <div ref={headRef} className={cn(styles.head, styles.reveal)}>
        <TitleBlock
          theme="dark"
          align="left"
          title={t('What the licence actually gives you')}
          description={t(
            'Written to be built on. The rights below sit in the standard schedule rather than behind a surcharge, an amendment, or a conversation you reopen at every renewal.',
          )}
        />
      </div>

      <div ref={tableWrapRef} className={cn(styles.tableWrap, styles.reveal)} onMouseMove={handleSpotlight}>
        <span className={styles.spotlight} aria-hidden="true" />
        <span className={cn(styles.corner, styles.cornerTl)} aria-hidden="true" />
        <span className={cn(styles.corner, styles.cornerBr)} aria-hidden="true" />

        <table className={styles.table}>
          <colgroup>
            <col className={styles.colRight} />
            <col className={styles.colOurs} />
            <col className={styles.colTypical} />
          </colgroup>
          <thead>
            <tr>
              <th className={styles.headRight}>{t('Right')}</th>
              <th className={styles.headOurs}>
                <span className={styles.badge}>{t('Standard schedule')}</span>
                <span className={styles.headOursName}>Global Database</span>
              </th>
              <th className={styles.headTypical}>{t('Typical market terms')}</th>
            </tr>
          </thead>
          <tbody>
            {rights.map((right) => (
              <tr key={right.label} className={styles.row}>
                <td className={styles.cellRight}>{right.label}</td>
                <td className={styles.cellOurs}>
                  <span className={styles.mobileLabel}>Global Database</span>
                  <span className={styles.oursInner}>
                    <span className={styles.tick} aria-hidden="true">
                      ✓
                    </span>
                    {right.ours}
                  </span>
                </td>
                <td className={styles.cellTypical}>
                  <span className={styles.mobileLabel}>{t('Typical market terms')}</span>
                  {right.typical}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Section>
  );
};
